import { useCallback, useEffect, useMemo, useState } from "react";
import Kanban, { type KanbanColumn } from "./Kanban";
import { Calendar, FolderOpen } from "lucide-react";
import { get } from "@/utils/service";
import { notify } from "@/components/ui/dialogs";
import { apiMessage, money, shortDate } from "@/utils/api";

export interface BoardDeal {
  id: string;
  title: string;
  amount: string;
  currency: string;
  stage_id: string;
  probability: number | null;
  effective_probability: number | null;
  expected_close_date: string | null;
  account?: { id: string; name: string } | null;
  owner?: { id: string; name: string } | null;
  [key: string]: unknown;
}

interface Column {
  stage: { id: string; name: string; category: "open" | "won" | "lost"; position: number };
  count: number;
  totals: Array<{ currency: string; amount: string }>;
  deals: BoardDeal[];
}

const initials = (name?: string | null) => (name ?? "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "—";

const accent = (category: Column["stage"]["category"]) =>
  category === "won" ? "bg-green-500" : category === "lost" ? "bg-red-400" : "bg-primary";

/**
 * Pipeline board: one column per active stage of the default pipeline. Cards are dragged (mouse,
 * touch or keyboard) or sent with "Move to" between columns; the page decides what a move does.
 */
export default function DealBoard({ refreshKey, canMove, onOpen, onMove }: {
  refreshKey: number;
  canMove: boolean;
  onOpen: (deal: BoardDeal) => void;
  /** Return false when the move needs confirming first; the card goes back until it is confirmed. */
  onMove: (deal: BoardDeal, toStageId: string) => Promise<boolean | void> | boolean | void;
}) {
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<{ data: { columns: Column[] } }>("/deals-board");
      setColumns(r.data.columns);
    } catch (e) {
      notify(apiMessage(e, "Could not load the pipeline."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const board = useMemo<KanbanColumn<BoardDeal>[]>(() => columns.map((col) => ({
    id: col.stage.id,
    title: col.stage.name,
    dot: accent(col.stage.category),
    items: col.deals,
    count: col.count,
    subtitle: col.totals.length ? col.totals.map((t) => money(t.amount, t.currency)).join(" · ") : "No value yet",
  })), [columns]);

  if (!loading && !columns.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white py-16 text-center">
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100"><FolderOpen className="h-8 w-8 text-gray-400" /></div>
        <h3 className="mb-2 text-lg font-light text-gray-900">No pipeline yet</h3>
        <p className="text-sm font-light text-gray-500">Set up a sales pipeline under Organization → Pipelines.</p>
      </div>
    );
  }

  return (
    <Kanban
      columns={board}
      loading={loading}
      noun="Deal"
      emptyText="No deals"
      getId={(d) => d.id}
      onOpen={onOpen}
      canMove={() => canMove}
      onMove={(deal, _from, to) => onMove(deal, to)}
      renderCard={(deal) => {
        const chance = deal.effective_probability ?? deal.probability;
        return (
          <div className="pr-6">
            <p className="text-sm font-semibold text-gray-900 line-clamp-2">{deal.title}</p>
            <p className="mt-0.5 truncate text-xs text-gray-500">{deal.account?.name ?? "No account"}</p>
            <p className="-mr-6 mt-3 border-t border-gray-100 pt-3 text-base font-semibold text-gray-900">{money(deal.amount, deal.currency)}</p>
            <div className="-mr-6 mt-2 flex items-center justify-between gap-2 text-[11px] text-gray-500">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />{deal.expected_close_date ? shortDate(deal.expected_close_date) : "No close date"}
              </span>
              <span className="flex items-center gap-2">
                {chance !== null && chance !== undefined && <span>{chance}%</span>}
                <span title={deal.owner?.name ?? "Unassigned"} className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">{initials(deal.owner?.name)}</span>
              </span>
            </div>
          </div>
        );
      }}
    />
  );
}
